import { useEffect } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { MonitorDown } from 'lucide-react';

// Components
import Button from '@/shared/components/elements/Button.jsx';
import Logo from '@/shared/components/modules/Logo.jsx';

// Assets
import logo from '@/assets/images/Logo.png';
import whiteLogo from '@/assets/images/WhiteLogo.png';

const instructionSteps = [
  {
    text: (
      <>
        در نوار آدرس مرورگر (بالای صفحه سمت راست)، روی آیکون{' '}
        <strong className="inline-flex items-center gap-1.5 font-semibold text-[var(--color-primary-100)]">
          <MonitorDown size={18} strokeWidth={2.5} />
        </strong>{' '}
        کلیک کنید.
      </>
    ),
  },
  {
    text: (
      <>
        در پنجره باز شده، روی دکمه{' '}
        <strong className="font-semibold text-gray-900 dark:text-white">
          Install
        </strong>{' '}
        کلیک کنید تا برنامه نصب شود.
      </>
    ),
  },
];

function InstructionStep({ text, stepNumber }) {
  return (
    <div className="flex items-start gap-5 rounded-2xl bg-white/50 p-5 transition-colors hover:bg-white sm:p-6 dark:bg-[#1c1f36]/50 dark:hover:bg-[#1c1f36]">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-primary-20)] text-xl font-bold text-[var(--color-primary-100)] dark:bg-[var(--color-primary-100)]/20 dark:text-[var(--color-primary-100)]">
        {stepNumber}
      </div>
      <div className="flex-1 pt-1.5">
        <p className="text-base leading-loose text-gray-700 dark:text-gray-300">
          {text}
        </p>
      </div>
    </div>
  );
}

export default function DesktopPwaPrompt({ showPrompt, dismissPrompt }) {
  useEffect(() => {
    if (showPrompt) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showPrompt]);

  const pageVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut',
        when: 'beforeChildren',
        staggerChildren: 0.15,
      },
    },
    exit: {
      opacity: 0,
      y: -15,
      transition: { duration: 0.3, ease: 'easeIn' },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          variants={pageVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="fixed inset-0 z-[9999] flex flex-col overflow-y-auto bg-gray-50/95 backdrop-blur-3xl select-none dark:bg-[#111322]/95"
        >
          {/* Header */}
          <header className="flex w-full items-center justify-between px-6 py-5 sm:px-10">
            <div className="flex-1" />

            <div className="flex flex-1 justify-center">
              <Logo
                src={logo}
                darkSrc={whiteLogo}
                alt="لوگو"
                imageClassName="h-10 w-auto object-contain sm:h-12"
              />
            </div>

            <div className="flex-1" />
          </header>

          {/* Main Content */}
          <main className="flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-6">
            <div className="w-full max-w-2xl">
              <motion.div variants={itemVariants} className="mb-10 text-center">
                <h1 className="mb-4 text-2xl font-bold text-gray-900 sm:text-3xl lg:text-4xl dark:text-white">
                  نصب اپلیکیشن اینتلیو
                </h1>
                <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                  برای دسترسی سریع‌تر و تجربه‌ای بهتر، اینتلیو را روی دستگاه خود
                  نصب کنید.
                </p>
              </motion.div>

              <motion.div
                variants={itemVariants}
                className="mb-12 overflow-hidden rounded-3xl border border-black/5 bg-black/[0.02] p-2 dark:border-white/5 dark:bg-white/[0.02]"
              >
                <div className="space-y-2">
                  {instructionSteps.map((step, index) => (
                    <InstructionStep
                      key={index}
                      stepNumber={index + 1}
                      text={step.text}
                    />
                  ))}
                </div>
              </motion.div>

              <motion.div
                variants={itemVariants}
                className="flex justify-center"
              >
                <Button
                  variant="primary"
                  size="xl"
                  onClick={dismissPrompt}
                  className="min-w-[280px] text-lg font-bold shadow-[0_8px_25px_rgba(239,89,38,0.35)] transition-transform hover:-translate-y-1 sm:min-w-[320px]"
                >
                  متوجه شدم
                </Button>
              </motion.div>
            </div>
          </main>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
